"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import {
  Home,
  ChevronRight,
  ChevronDown,
  PlaneLanding,
  PlaneTakeoff,
  Search,
  ShoppingBag,
  UtensilsCrossed,
  Sofa,
  Wifi,
  Sparkles,
  ArrowDown,
  ArrowRight,
  ShieldCheck,
  Mail,
  Phone,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { ToastContainer } from "@/components/Toast";
import { useToast } from "@/hooks/useToast";
import { fetchVuelos } from "@/lib/api/vuelos/fetchVuelos";
import type { Vuelos } from "@/lib/api/vuelos/types";
import { saveConnectionSelection } from "./connectionStorage";
import { intlTag } from "@/i18n/config";

const NAVY = "#102542";
const YELLOW = "#ffb800";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Generales/9M1A0564.jpg";

const COPA_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Movimiento%20de%20pasajeros/321A9522.jpg";

const ACTIVITY_ICONS: Record<string, LucideIcon> = {
  shopping: ShoppingBag,
  restaurant: UtensilsCrossed,
  lounge: Sofa,
  wifi: Wifi,
};

type Activity = {
  icon: string;
  title: string;
  description: string;
  time: string;
  href: string;
};

type CopaFeature = { title: string; description: string };
type Tip = { title: string; description: string };

export default function ConexionPage() {
  const t = useTranslations("Conexion");
  const tCrumb = useTranslations("breadcrumb");
  const locale = useLocale();
  const { toasts, showToast, closeToast } = useToast();

  const activities = t.raw("activities.items") as Activity[];
  const copaFeatures = t.raw("copa.features") as CopaFeature[];
  const tips = t.raw("tips.items") as Tip[];

  // Vuelos
  const [arrivals, setArrivals] = useState<Vuelos[]>([]);
  const [departures, setDepartures] = useState<Vuelos[]>([]);
  const [loading, setLoading] = useState(true);

  // Selección
  const [vueloLlegada, setVueloLlegada] = useState<Vuelos | null>(null);
  const [vueloSalida, setVueloSalida] = useState<Vuelos | null>(null);
  const [acepta, setAcepta] = useState(false);

  // Dropdowns
  const [llegadaOpen, setLlegadaOpen] = useState(false);
  const [salidaOpen, setSalidaOpen] = useState(false);
  const [qLlegada, setQLlegada] = useState("");
  const [qSalida, setQSalida] = useState("");

  const router = useRouter();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchVuelos("arrivals", "hoy", "pty"),
      fetchVuelos("arrivals", "mañana", "pty"),
      fetchVuelos("departures", "hoy", "pty"),
      fetchVuelos("departures", "mañana", "pty"),
    ])
      .then(([arrHoy, arrMan, depHoy, depMan]) => {
        setArrivals([...arrHoy, ...arrMan]);
        setDepartures([...depHoy, ...depMan]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatVuelo = (v: Vuelos) => {
    const codigo = `${v.vuelo}${v.vueloNum}`.trim();
    const dt = v.horaEstimada ? new Date(v.horaEstimada) : null;
    const hora = dt
      ? dt.toLocaleTimeString(intlTag(locale), {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";
    return { codigo, hora, dt };
  };

  const filtrar = (lista: Vuelos[], q: string) => {
    if (!q) return lista.slice(0, 80);
    const query = q.toLowerCase();
    return lista
      .filter((v) => {
        const { codigo } = formatVuelo(v);
        return (
          codigo.toLowerCase().includes(query) ||
          (v.routingDescEs || "").toLowerCase().includes(query) ||
          (v.routing || "").toLowerCase().includes(query) ||
          (v.aerolinea || "").toLowerCase().includes(query)
        );
      })
      .slice(0, 80);
  };

  const arrivalsFiltradas = useMemo(
    () => filtrar(arrivals, qLlegada),
    [arrivals, qLlegada],
  );
  const departuresFiltradas = useMemo(
    () => filtrar(departures, qSalida),
    [departures, qSalida],
  );

  const scrollToPlanner = () => {
    document
      .getElementById("planificador")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCalcular = () => {
    if (!vueloLlegada || !vueloSalida || !acepta) {
      showToast(
        "warning",
        t("planner.missingTitle"),
        t("planner.missingDesc"),
      );
      return;
    }

    if (
      vueloLlegada.id === vueloSalida.id &&
      vueloLlegada.vuelo === vueloSalida.vuelo &&
      vueloLlegada.vueloNum === vueloSalida.vueloNum
    ) {
      showToast(
        "warning",
        t("planner.sameFlightTitle"),
        t("planner.sameFlightDesc"),
      );
      return;
    }

    const arr = new Date(vueloLlegada.horaEstimada);
    const dep = new Date(vueloSalida.horaEstimada);
    const totalMin = Math.round((dep.getTime() - arr.getTime()) / 60000);

    if (totalMin <= 0) {
      showToast(
        "warning",
        t("planner.invalidOrderTitle"),
        t("planner.invalidOrderDesc"),
      );
      return;
    }

    // Guardamos la selección y abrimos la página de resultado, que carga
    // toda la información de la escala (vuelos, tiempo, locales, mapa, etc.).
    saveConnectionSelection({
      arrival: vueloLlegada,
      departure: vueloSalida,
    });
    router.push(`/${locale}/conexion/resultado`);
  };

  // Dropdown reusable
  type DropProps = {
    label: string;
    valor: Vuelos | null;
    onSelect: (v: Vuelos) => void;
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    busqueda: string;
    setBusqueda: (v: string) => void;
    vuelos: Vuelos[];
    Icon: LucideIcon;
  };

  const VueloDropdown = (props: DropProps) => {
    const { codigo, hora } = props.valor
      ? formatVuelo(props.valor)
      : { codigo: "", hora: "" };

    return (
      <div className="flex flex-col relative">
        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
          <props.Icon className="w-3.5 h-3.5" />
          {props.label}
        </label>
        <button
          type="button"
          onClick={props.onToggle}
          className="bg-white border border-gray-200 hover:border-gray-400 focus:border-[#102542] rounded-lg px-3.5 py-3 text-sm flex items-center justify-between text-left transition-colors focus:outline-none"
        >
          {props.valor ? (
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-[#102542] truncate">
                {codigo} · {props.valor.routingDescEs || props.valor.routing}
              </span>
              <span className="text-xs text-gray-500 font-mono">
                {hora} · {props.valor.aerolinea}
              </span>
            </div>
          ) : (
            <span className="text-gray-400">{t("planner.vueloPh")}</span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
              props.isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {props.isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={props.busqueda}
                  onChange={(e) => props.setBusqueda(e.target.value)}
                  placeholder={t("planner.buscarVuelo")}
                  className="w-full border border-gray-200 rounded-md py-2 pl-8 pr-2 text-sm focus:border-[#102542] focus:outline-none"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto">
              {loading && (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  {t("planner.cargandoVuelos")}
                </div>
              )}
              {!loading && props.vuelos.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  {t("planner.sinVuelos")}
                </div>
              )}
              {!loading &&
                props.vuelos.map((v) => {
                  const { codigo, hora } = formatVuelo(v);
                  const isCopa = (v.vuelo || "").toUpperCase() === "CM";
                  return (
                    <button
                      key={`${v.id}-${codigo}-${v.horaEstimada}`}
                      type="button"
                      onClick={() => {
                        props.onSelect(v);
                        props.onClose();
                        props.setBusqueda("");
                      }}
                      className="w-full text-left px-3.5 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-2"
                    >
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-[#102542] text-sm truncate flex items-center gap-2">
                          {codigo}
                          {isCopa && (
                            <span
                              className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: YELLOW, color: NAVY }}
                            >
                              Copa
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500 truncate">
                          {v.routingDescEs || v.routing}
                        </span>
                      </div>
                      {hora && (
                        <span className="text-xs text-gray-500 shrink-0 font-mono">
                          {hora}
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("hero.title")}
          fill
          priority
          sizes="100vw"
          className="object-cover blur-xs"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>{tCrumb("home")}</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li
                aria-current="page"
                className="text-white font-medium truncate"
              >
                {t("breadcrumb")}
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px" style={{ backgroundColor: YELLOW }} />
              {t("hero.eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("hero.title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Hero CTA bar */}
      <section className="bg-[#102542]">
        <div className="max-w-[80%] mx-auto py-8 md:py-10">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-2 text-white/60">
                <span
                  className="w-8 h-px"
                  style={{ backgroundColor: YELLOW }}
                />
                {t("heroCta.label")}
              </span>
              <p className="text-white text-base md:text-lg leading-relaxed">
                {t("heroCta.description")}
              </p>
            </div>
            <button
              type="button"
              onClick={scrollToPlanner}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: YELLOW, color: NAVY }}
            >
              {t("heroCta.button")}
              <ArrowDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Intro: Hub de las Américas */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 items-start">
            <div className="max-w-3xl">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("intro.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                {t("intro.title")}
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                {t("intro.subtitle")}
              </p>
            </div>

            <div className="flex flex-col gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200 lg:min-w-[280px]">
              {[1, 2, 3].map((n) => (
                <div key={n} className="bg-white p-6">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">
                    {t(`intro.highlight${n}Label`)}
                  </p>
                  <p
                    className="text-3xl font-bold tabular-nums tracking-tight"
                    style={{ color: NAVY }}
                  >
                    {t(`intro.highlight${n}Value`)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t(`intro.highlight${n}Note`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Planificador */}
      <section
        id="planificador"
        className="relative scroll-mt-24 overflow-hidden"
      >
        <div className="absolute inset-0">
          <Image
            src={HERO_IMAGE}
            alt=""
            fill
            sizes="100vw"
            className="object-cover blur-md scale-110"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75" />
        </div>

        <div className="relative max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4 text-white">
              <span className="w-8 h-px" style={{ backgroundColor: YELLOW }} />
              {t("planner.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
              {t("planner.title")}
            </h2>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("planner.subtitle")}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCalcular();
            }}
            className="bg-white/95 backdrop-blur-sm rounded-2xl border border-gray-200 p-8 md:p-12 max-w-4xl mx-auto shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <VueloDropdown
                label={t("planner.vueloLlegadaLabel")}
                valor={vueloLlegada}
                onSelect={setVueloLlegada}
                isOpen={llegadaOpen}
                onToggle={() => {
                  setLlegadaOpen((v) => !v);
                  setSalidaOpen(false);
                }}
                onClose={() => setLlegadaOpen(false)}
                busqueda={qLlegada}
                setBusqueda={setQLlegada}
                vuelos={arrivalsFiltradas}
                Icon={PlaneLanding}
              />
              <VueloDropdown
                label={t("planner.vueloSalidaLabel")}
                valor={vueloSalida}
                onSelect={setVueloSalida}
                isOpen={salidaOpen}
                onToggle={() => {
                  setSalidaOpen((v) => !v);
                  setLlegadaOpen(false);
                }}
                onClose={() => setSalidaOpen(false)}
                busqueda={qSalida}
                setBusqueda={setQSalida}
                vuelos={departuresFiltradas}
                Icon={PlaneTakeoff}
              />
            </div>

            {/* Aviso de responsabilidad */}
            <div
              className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4"
              style={{ borderLeft: `3px solid ${YELLOW}` }}
            >
              <div className="flex items-start gap-3">
                <ShieldCheck
                  className="w-5 h-5 mt-0.5 shrink-0"
                  style={{ color: NAVY }}
                />
                <div className="flex-1">
                  <p
                    className="text-xs font-semibold uppercase tracking-wider mb-1"
                    style={{ color: NAVY }}
                  >
                    {t("planner.agreementTitle")}
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {t("planner.agreementText")}
                  </p>
                </div>
              </div>
              <label className="mt-4 flex items-start gap-3 cursor-pointer select-none group">
                <span className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={acepta}
                    onChange={(e) => setAcepta(e.target.checked)}
                    className="peer sr-only"
                  />
                  <span
                    className="w-5 h-5 rounded border-2 border-gray-300 peer-checked:bg-[#102542] peer-checked:border-[#102542] transition-colors flex items-center justify-center group-hover:border-gray-400"
                  >
                    {acepta && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </span>
                </span>
                <span className="text-sm text-gray-700 leading-relaxed">
                  {t("planner.agreementAccept")}
                </span>
              </label>
            </div>

            <div className="flex justify-end mt-6 pt-6 border-t border-gray-100">
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors hover:opacity-90 whitespace-nowrap"
                style={{ backgroundColor: YELLOW, color: NAVY }}
              >
                <Search className="w-3.5 h-3.5" />
                {t("planner.submit")}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Actividades */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("activities.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("activities.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("activities.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {activities.map((act, idx) => {
              const Icon = ACTIVITY_ICONS[act.icon] ?? ShoppingBag;
              return (
                <Link
                  key={act.title}
                  href={`/${locale}${act.href}`}
                  className="group relative bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
                >
                  <span
                    className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                    style={{ backgroundColor: YELLOW }}
                  />
                  <div className="flex items-start justify-between mb-6">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "#f5f5f5", color: NAVY }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <h3
                    className="text-lg font-bold tracking-tight mb-3"
                    style={{ color: NAVY }}
                  >
                    {act.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    {act.description}
                  </p>
                  <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                      {act.time}
                    </span>
                    <ArrowRight
                      className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                      style={{ color: NAVY }}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Copa Hub */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("copa.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                {t("copa.title")}
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                {t("copa.subtitle")}
              </p>

              <ul className="flex flex-col gap-5">
                {copaFeatures.map((f) => (
                  <li
                    key={f.title}
                    className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 bg-white"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                      style={{
                        backgroundColor: "rgba(255,184,0,0.15)",
                        color: NAVY,
                      }}
                    >
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h3
                        className="text-sm font-bold tracking-tight mb-1"
                        style={{ color: NAVY }}
                      >
                        {f.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {f.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5 lg:col-start-8 relative aspect-[4/5] rounded-2xl overflow-hidden border border-gray-200">
              <Image
                src={COPA_IMAGE}
                alt={t("copa.title")}
                fill
                sizes="(max-width: 1024px) 100vw, 40vw"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span
                  className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-full"
                  style={{ backgroundColor: YELLOW, color: NAVY }}
                >
                  <Sparkles className="w-3 h-3" />
                  Hub de las Américas
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("tips.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("tips.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("tips.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {tips.map((tip, idx) => (
              <article
                key={tip.title}
                className="bg-white p-10 flex flex-col gap-4 transition-colors hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className="text-4xl font-bold tabular-nums tracking-tight leading-none"
                    style={{ color: NAVY }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="flex-1 h-px"
                    style={{ backgroundColor: YELLOW, maxWidth: 48 }}
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-900 tracking-tight">
                  {tip.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tip.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Ayuda */}
      <section className="bg-[#102542]">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
                <span
                  className="w-8 h-px"
                  style={{ backgroundColor: YELLOW }}
                />
                {t("ayuda.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                {t("ayuda.title")}
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed">
                {t("ayuda.subtitle")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              <a
                href={`https://wa.me/${t("ayuda.whatsapp").replace(/[^\d]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <FaWhatsapp className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("ayuda.whatsappLabel")}
                  </p>
                  <p className="text-white font-semibold tabular-nums">
                    {t("ayuda.whatsapp")}
                  </p>
                </div>
              </a>

              <a
                href={`tel:${t("ayuda.phone").replace(/[^\d+]/g, "")}`}
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <Phone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("ayuda.phoneLabel")}
                  </p>
                  <p className="text-white font-semibold tabular-nums">
                    {t("ayuda.phone")}
                  </p>
                </div>
              </a>

              <a
                href={`mailto:${t("ayuda.email")}`}
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30]"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("ayuda.emailLabel")}
                  </p>
                  <p className="text-white font-semibold truncate">
                    {t("ayuda.email")}
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <ToastContainer toasts={toasts} onClose={closeToast} />
    </main>
  );
}
